import { type i18n, exists, type InitOptions, type TFunction } from 'i18next';
import { deepMerge } from '../../scripts/deepMerge';
import { getQueryParamsFromURL } from './urlHelper';

export const defaultLocale = 'en';
export const defaultNamespace = 'translations';

export const i18nOptions = {
  fallbackLng: defaultLocale,
  ns: [defaultNamespace],
  defaultNS: defaultNamespace,
  nsSeparator: false,
  debug: false,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
  initAsync: false,
} satisfies InitOptions;

export const TRANSLATION_FILE_NAMES = [
  'translations-boka.json',
  'translations-contact-form.json',
];

const { country, language } = CSS_CONFIG;

const getTranslationUrl = (filename: string): string => {
  const host = TARGET === 'prod' ? 'www.ikea.com' : 'www.cte.ikeadt.com';
  return `https://${host}/${country}/${language}/appointment/translations/${filename}`;
};

const fetchTranslationFile = async (
  url: string,
): Promise<Record<string, unknown>> => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Failed to load translations from ${url}: ${response.status}`,
    );
  }

  const data = await response.json();
  if (!data.timestampBokaDeploy || data.timestampBokaDeploy < TIMESTAMP) {
    throw new Error(`Translation file from ${url} is outdated`);
  }
  return data;
};

export const loadRuntimeTranslations = async (i18n: i18n): Promise<void> => {
  const showTranslationKeys =
    typeof window !== 'undefined' && getQueryParamsFromURL().translationsKeys;
  if (showTranslationKeys) {
    i18n.changeLanguage('cimode');
    return;
  }

  if (TARGET !== 'stage' && TARGET !== 'prod') {
    return;
  }

  const translationPromises = TRANSLATION_FILE_NAMES.map((filename) => {
    const url = getTranslationUrl(filename);
    return fetchTranslationFile(url);
  });

  try {
    const translationFiles = await Promise.all(translationPromises);

    const runtimeTranslations = translationFiles.reduce(
      (merged, translations) => deepMerge(merged, translations),
      {},
    );

    i18n.addResourceBundle(
      CSS_CONFIG.language,
      defaultNamespace,
      runtimeTranslations,
      false,
      true,
    );
  } catch (error: unknown) {
    if (error instanceof Error) {
      console.warn(
        'Failed to load runtime translations, using build-time fallback:',
        error.message,
      );
    } else {
      console.warn(
        'Failed to load runtime translations, using build-time fallback:',
        error,
      );
    }
  }
};

/**
 * Checks if a translation key exists and has a non-empty value (excluding whitespace-only strings).
 * Extends i18next's exists() to also validate that the translation isn't just whitespace.
 */
export const translationExists = (key: string, t: TFunction): boolean => {
  if (!exists(key)) {
    return false;
  }
  const translation = t(key);
  return typeof translation === 'string' && translation.trim() !== '';
};
